import 'react-loading-skeleton/dist/skeleton.css';
import { css } from '@emotion/react';
import { TripType, CepData, BookingFlow } from '@flysas-tech/book-value-stream-models';
import { BREAKPOINTS, Button, IconButton, tokens } from '@flysas-tech/commercial-ui';
import { useEffect, useState } from 'react';
import { capitalizeFirstLetter } from 'shared';

import { Section } from '../atoms';

import { Cep } from './Cep';
import { CepEvent } from './constants';
import { Footer } from './Footer';
import { HeaderLogoIcon } from './icons';
import { LanguagesDropdown } from './LanguagesDropdown';
import { Menu } from './Menu';

const styles = {
  wrapper: css`
    width: 100%;
    display: flex;
    align-items: stretch;
    min-height: 100vh;
  `,
  page: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;

    @media (min-width: ${BREAKPOINTS.layout.md}px) {
      overflow-y: hidden;
    }
  `,
  header: css`
    height: 72px;
    padding: 0 ${tokens.dimension['space-6']};
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: ${tokens.color.white};

    @media (min-width: ${BREAKPOINTS.layout.md}px) {
      padding: 0 ${tokens.dimension['space-10']};
    }
  `,
  content: css`
    flex: 1;
    position: relative;
  `,
  headerRight: css`
    position: relative;
    display: flex;
    align-items: center;
    gap: 16px;
  `,
  menuButton: css`
    @media (min-width: ${BREAKPOINTS.layout.md}px) {
      display: none;
    }
  `,

  cep: css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    padding: ${tokens.dimension['space-6']};
    z-index: 12;
    background-color: ${tokens.color.white};
  `,
  innerContainer: css`
    display: grid;
    grid-column: main;
  `,
};

export function SimulatedHostLayout({
  children,
  bookingFlow = BookingFlow.REVENUE,
  showFooter = false,
}: {
  children: React.ReactNode;
  bookingFlow?: BookingFlow;
  showFooter?: boolean;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showCep, setShowCep] = useState(false);
  const [cepData, setCepData] = useState<Partial<CepData>>({});

  const handleSearch = (data: CepData) => {
    const event = new CustomEvent(CepEvent.SEARCH, {
      detail: {
        ...data,
        origin: data.origin?.toUpperCase(),
        destination: data.destination?.toUpperCase(),
      },
    });
    window.dispatchEvent(event);
    setShowCep(false);
  };

  useEffect(() => {
    const handleShowCepEvent = (event: Event) => {
      const customEvent = event as CustomEvent<CepData>;
      setCepData(
        customEvent.detail ?? {
          tripType: TripType.ONE_WAY,
          passengerCounts: { adults: 1, children: 0, infants: 0 },
        }
      );
      setShowCep(true);
    };

    window.addEventListener(CepEvent.SHOW, handleShowCepEvent);
    return () => {
      window.removeEventListener(CepEvent.SHOW, handleShowCepEvent);
    };
  }, []);

  useEffect(() => {
    const handleHideCepEvent = () => {
      setShowCep(false);
    };

    window.addEventListener(CepEvent.HIDE, handleHideCepEvent);
    return () => {
      window.removeEventListener(CepEvent.HIDE, handleHideCepEvent);
    };
  }, []);

  return (
    <div css={styles.wrapper}>
      <Menu isOpen={showMenu} setIsOpen={setShowMenu} />

      <div css={styles.page}>
        <header css={styles.header}>
          <HeaderLogoIcon />

          <div css={styles.headerRight}>
            {showLanguageDropdown && <LanguagesDropdown />}
            <IconButton
              ariaLabel="language"
              size="medium"
              variant="link"
              icon="Globe"
              onClick={() => setShowLanguageDropdown(prev => !prev)}
            />
            <Button css={styles.menuButton} size="medium" variant="secondary" onClick={() => setShowMenu(!showMenu)}>
              {capitalizeFirstLetter('menu')}
            </Button>
          </div>
        </header>

        <div css={styles.content}>
          {showCep && (
            <>
              <div css={styles.cep}>
                <Section>
                  <Cep handleSearch={handleSearch} data={cepData} bookingFlow={bookingFlow} />
                </Section>
              </div>
            </>
          )}

          <div>{children}</div>

          {showFooter && <Footer />}
        </div>
      </div>
    </div>
  );
}
